/* Wizard · Lab Section primitives — extracted iter101.91 from
 * `/app/frontend/src/pages/doctor/CaseSubmissionPage.jsx` (was inline
 * inside the ~9,000-line wizard file).
 *
 * These are the low-level visual atoms used by Step 4 (Treatment Plan
 * accordions) and Step 5 (Lab Instructions accordions):
 *   · LabSection           — Accordion shell with icon tile + reset chip
 *   · LabChip              — Small toggle chip
 *   · LabChoiceCard        — Chunky 3-up single-pick card
 *   · LabInfoNote          — Callout note inside a section
 *   · LabGlanceRow         — Key/value row inside a summary panel
 *   · FdiChipPicker        — FDI tooth-number toggle grid
 *   · PhaseSplitVisualizer — Shipment phase split slider
 */
import React, { useState, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { ArrowsCounterClockwise, CaretDown, Check, Info } from "@phosphor-icons/react";
import { useSettlePulse } from "./hooks";


export function LabSection({ icon, tone = "teal", title, desc, badge, children, testid, onReset = null, defaultOpen = false, summary = null }) {
    const [open, setOpen] = useState(defaultOpen);
    /* iter98.99t — Aligned to the same shell as <ExamAccordion> (Step 2)
       and <AccordionCard> (Step 3) so the wizard accordions read as
       one consistent stack across the entire flow. */
    const toneBg = {
        teal: "bg-emerald-100 text-emerald-700",
        amber: "bg-amber-100 text-amber-700",
        indigo: "bg-indigo-100 text-indigo-700",
        rose: "bg-rose-100 text-rose-700",
        slate: "bg-slate-100 text-slate-700",
        violet: "bg-violet-100 text-violet-700",
        cyan: "bg-cyan-100 text-cyan-700",
        red: "bg-red-100 text-red-600",
    }[tone] || "bg-emerald-100 text-emerald-700";
    return (
        <section
            className="group relative bg-card border-[0.5px] border-border rounded-[12px] overflow-hidden transition-all duration-150 hover:shadow-sm hover:border-foreground/15 mb-2.5"
            data-testid={testid}
            data-open={open ? "1" : "0"}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none text-left hover:bg-muted/30 transition-colors ${open ? "border-b-[0.5px] border-border" : ""}`}
                data-testid={`${testid}-header`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0 ${toneBg}`}>
                        {icon}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-[13.5px] font-semibold leading-tight truncate">{title}</h3>
                            {badge && (
                                <span className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-primary/10 text-primary border-[0.5px] border-primary/30">
                                    {badge}
                                </span>
                            )}
                            {!open && summary && (
                                <span className="text-[10.5px] font-medium px-2 py-0.5 rounded-full bg-secondary/60 text-foreground/75 border-[0.5px] border-border/60">
                                    {summary}
                                </span>
                            )}
                        </div>
                        {open && desc && (
                            <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{desc}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {open && onReset && (
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); onReset(); }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onReset(); } }}
                            className="text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md border-[0.5px] border-border hover:border-foreground/30 inline-flex items-center gap-1 cursor-pointer"
                            data-testid={`${testid}-reset`}
                            title="Reset this section to defaults"
                        >
                            <ArrowsCounterClockwise size={11} weight="bold" />
                            <span>Reset</span>
                        </span>
                    )}
                    <CaretDown size={13} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </button>
            {open && (
                <div className="px-4 py-3.5">
                    {children}
                </div>
            )}
        </section>
    );
}

export function LabChip({ icon, label, suffix, selected, onClick, testid }) {
    /* iter98.99ai — Shared settle-pulse so pill toggles feel as
       "locked-in" as the big cards. Ring is scaled down (`ring-2`)
       to match the chip's tiny footprint. */
    const flashCls = useSettlePulse(selected, "ring-primary/25");
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            aria-pressed={selected}
            className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border-[0.5px] mr-1.5 mb-1.5 transition-all duration-300 ${flashCls} ${
                selected
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-border bg-card text-foreground/70 hover:bg-secondary/40 hover:border-foreground/25"
            }`}
        >
            {icon}
            <span>{label}</span>
            {suffix && <span className="text-[10px] opacity-60 ml-0.5">{suffix}</span>}
        </button>
    );
}

/* iter98.99r — `LabChoiceCard` is the big-format replacement for
   `LabChip` used in high-stakes single-pick rows (Arches, Wear schedule). */
export function LabChoiceCard({ icon, label, sub, selected, onClick, testid, accent = "teal", suffix = null }) {
    const ACCENT = {
        teal: {
            cardOn:   "border-primary bg-primary/[0.06] ring-2 ring-primary/30",
            cardOff:  "border-border bg-card hover:border-primary/40 hover:bg-primary/[0.03] hover:shadow-sm",
            iconOn:   "bg-primary text-white",
            iconOff:  "bg-primary/10 text-primary",
            labelOn:  "text-primary font-semibold",
            labelOff: "text-foreground",
            subOn:    "text-primary/80",
            subOff:   "text-muted-foreground",
            checkBg:  "bg-primary text-white",
        },
        violet: {
            cardOn:   "border-violet-500 bg-violet-50 ring-2 ring-violet-400/30",
            cardOff:  "border-border bg-card hover:border-violet-400 hover:bg-violet-50/40 hover:shadow-sm",
            iconOn:   "bg-violet-500 text-white",
            iconOff:  "bg-violet-100 text-violet-700",
            labelOn:  "text-violet-900 font-semibold",
            labelOff: "text-foreground",
            subOn:    "text-violet-700/80",
            subOff:   "text-muted-foreground",
            checkBg:  "bg-violet-500 text-white",
        },
        amber: {
            cardOn:   "border-amber-500 bg-amber-50 ring-2 ring-amber-400/30",
            cardOff:  "border-border bg-card hover:border-amber-400 hover:bg-amber-50/40 hover:shadow-sm",
            iconOn:   "bg-amber-500 text-white",
            iconOff:  "bg-amber-100 text-amber-700",
            labelOn:  "text-amber-900 font-semibold",
            labelOff: "text-foreground",
            subOn:    "text-amber-700/80",
            subOff:   "text-muted-foreground",
            checkBg:  "bg-amber-500 text-white",
        },
    }[accent] || {
        cardOn: "border-primary bg-primary/[0.06] ring-2 ring-primary/30",
        cardOff: "border-border bg-card hover:border-primary/40 hover:shadow-sm",
        iconOn: "bg-primary text-white",
        iconOff: "bg-primary/10 text-primary",
        labelOn: "text-primary font-semibold",
        labelOff: "text-foreground",
        subOn: "text-primary/80",
        subOff: "text-muted-foreground",
        checkBg: "bg-primary text-white",
    };
    /* iter98.99ai — Settle-pulse. Accent-aware ring color so the
       flash matches the card's on-state family (teal / violet / amber). */
    const flashRing = {
        teal:   "ring-primary/30",
        violet: "ring-violet-400/35",
        amber:  "ring-amber-400/35",
    }[accent] || "ring-primary/30";
    const flashCls = useSettlePulse(selected, flashRing);
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            aria-pressed={selected}
            className={[
                "relative w-full text-left rounded-xl border-2 p-3 transition-all duration-300 cursor-pointer overflow-hidden",
                selected ? ACCENT.cardOn : ACCENT.cardOff,
                flashCls,
            ].join(" ")}
        >
            <div className="flex items-start gap-2.5">
                <div className={`shrink-0 w-10 h-10 rounded-lg grid place-items-center transition-colors ${selected ? ACCENT.iconOn : ACCENT.iconOff}`}>
                    {icon}
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`text-[13px] leading-tight ${selected ? ACCENT.labelOn : ACCENT.labelOff}`}>
                        {label}
                        {suffix && <span className="text-[10.5px] font-normal opacity-70 ml-1">{suffix}</span>}
                    </div>
                    {sub && (
                        <div className={`text-[10.5px] mt-1 leading-snug ${selected ? ACCENT.subOn : ACCENT.subOff}`}>
                            {sub}
                        </div>
                    )}
                </div>
                {selected && (
                    <div className={`absolute top-2 right-2 w-5 h-5 rounded-full grid place-items-center ${ACCENT.checkBg} shadow-sm`}>
                        <Check size={11} weight="bold" />
                    </div>
                )}
            </div>
        </button>
    );
}

export function LabInfoNote({ tone = "teal", children }) {
    const toneClass = {
        teal: "bg-primary/[0.06] border-primary/30 text-primary",
        amber: "bg-amber-50 border-amber-300 text-amber-800",
        violet: "bg-violet-50 border-violet-300 text-violet-800",
    }[tone] || "bg-primary/[0.06] border-primary/30 text-primary";
    return (
        <div className={`flex gap-2 items-start p-2.5 rounded-lg mt-2.5 border-[0.5px] ${toneClass}`}>
            <Info size={14} className="shrink-0 mt-0.5" weight="fill" />
            <p className="text-[11.5px] leading-snug">{children}</p>
        </div>
    );
}

export function LabGlanceRow({ k, v, last = false }) {
    return (
        <div className={`flex items-baseline justify-between py-1.5 ${last ? "" : "border-b-[0.5px] border-border/40"}`}>
            <span className="text-[11.5px] text-muted-foreground">{k}</span>
            <span className="text-[12px] font-medium text-foreground text-right max-w-[60%] truncate" title={typeof v === "string" ? v : ""}>{v}</span>
        </div>
    );
}

/* iter97.76 — FDI quick-tap chip picker for "Do not move teeth". */
export function FdiChipPicker({ value, onChange, testid }) {
    const text = String(value || "");
    const selected = useMemo(() => {
        const set = new Set();
        const re = /\b(1[1-8]|2[1-8]|3[1-8]|4[1-8])\b/g;
        let m;
        while ((m = re.exec(text)) !== null) set.add(m[1]);
        return set;
    }, [text]);
    const toggle = (fdi) => {
        const s = String(fdi);
        if (selected.has(s)) {
            const re = new RegExp(`(\\s*,\\s*)?\\b${s}\\b`, "g");
            const next = text.replace(re, "").replace(/^\s*,\s*/, "").trim();
            onChange(next);
        } else {
            const dashIdx = text.indexOf("—");
            const prefix = dashIdx >= 0 ? text.slice(0, dashIdx).trim() : text.trim();
            const suffix = dashIdx >= 0 ? text.slice(dashIdx) : "";
            const nums = prefix ? `${prefix}, ${s}` : s;
            const combined = suffix ? `${nums} ${suffix}` : nums;
            onChange(combined);
        }
    };
    const rows = [
        { label: "Upper Right", teeth: [18, 17, 16, 15, 14, 13, 12, 11] },
        { label: "Upper Left", teeth: [21, 22, 23, 24, 25, 26, 27, 28] },
        { label: "Lower Left", teeth: [38, 37, 36, 35, 34, 33, 32, 31] },
        { label: "Lower Right", teeth: [41, 42, 43, 44, 45, 46, 47, 48] },
    ];
    return (
        <div className="space-y-2" data-testid={testid}>
            {rows.map((row) => (
                <div key={row.label} className="flex items-center gap-2">
                    <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground w-24 shrink-0">
                        {row.label}
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {row.teeth.map((t) => {
                            const isOn = selected.has(String(t));
                            return (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => toggle(t)}
                                    data-testid={`fdi-chip-${t}`}
                                    aria-pressed={isOn}
                                    className={`w-9 h-8 rounded-[6px] border-[0.5px] text-[11px] font-semibold tabular-nums transition-colors ${
                                        isOn
                                            ? "border-slate-700 bg-slate-700 text-white shadow-sm"
                                            : "border-border bg-card text-foreground/70 hover:border-foreground/40 hover:bg-secondary/40"
                                    }`}
                                >
                                    {t}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

/* iter97.76 — Phase split visualizer. Shows a horizontal bar
   representing the total aligner sequence with a draggable split
   point. The split is stored on rx.shipment_phase_split. */
export function PhaseSplitVisualizer({ total, value, onChange }) {
    const tot = Math.max(1, Number(total) || 0);
    const safeValue = Math.min(Math.max(Number(value) || Math.ceil(tot / 2), 1), Math.max(tot - 1, 1));
    const pct = Math.round((safeValue / tot) * 100);
    return (
        <div className="mt-3" data-testid="phase-split-visualizer">
            <Label className="text-[11px] text-muted-foreground mb-2 block">
                Phase split point — Phase 1 ends at aligner #
            </Label>
            <div className="flex items-center gap-3">
                <input
                    type="range"
                    min={1}
                    max={Math.max(tot - 1, 1)}
                    step={1}
                    value={safeValue}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="flex-1 accent-amber-500"
                    data-testid="phase-split-slider"
                />
                <div className="text-[12px] font-bold tabular-nums text-amber-700 min-w-[40px] text-right">
                    {safeValue}
                </div>
            </div>
            <div className="relative mt-2 h-7 rounded-[8px] border-[0.5px] border-amber-200 overflow-hidden bg-white">
                <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-amber-500"
                    style={{ width: `${pct}%` }}
                />
                <div
                    className="absolute inset-y-0 bg-gradient-to-r from-amber-200 to-amber-300"
                    style={{ left: `${pct}%`, right: 0 }}
                />
                <div className="absolute inset-0 flex items-center justify-between px-2 text-[10px] font-semibold text-amber-900">
                    <span>Phase 1 · 1-{safeValue}</span>
                    <span>Phase 2 · {safeValue + 1}-{tot}</span>
                </div>
            </div>
            <p className="text-[10.5px] text-muted-foreground mt-1.5">
                Phase 1 ships first, Phase 2 ships once patient returns for mid-treatment review.
            </p>
        </div>
    );
}
